import type { GameEvent } from '../core';
import { QUIET_POWERUPS } from '../ui/reactions';

/**
 * Brandt-approved cheers. The files are already loudness-normalized, so
 * playback stays at unity on the chime bus. Voice ducks that bus, which
 * puts the cheer under the line. Applause is SFX: the voice gate does not
 * drop it, and a second cheer waits until the one that is playing ends.
 *
 * Light is the quieter pool (-22 LUFS). Medium is -19 LUFS.
 */
export const LIGHT_APPLAUSE = [
  'applause-11',
  'applause-13',
  'applause-14',
  'applause-15',
  'applause-16',
  'applause-17',
] as const;

export const MEDIUM_APPLAUSE = [
  'applause-06',
  'applause-07',
  'applause-18',
  'applause-19',
  'applause-20',
  'applause-21',
  'applause-23',
] as const;

export const APPLAUSE_FILES = [...LIGHT_APPLAUSE, ...MEDIUM_APPLAUSE] as const;

export type ApplausePool = 'light' | 'medium';

const POOLS: Record<ApplausePool, readonly string[]> = {
  light: LIGHT_APPLAUSE,
  medium: MEDIUM_APPLAUSE,
};

/**
 * Which cheer a human move asks for.
 *
 * Match-4, a short cascade (the first two extra waves), and an ordinary
 * trick are light. Match-5, a long cascade (wave 3 and after), and a strong
 * trick are medium. The announcer's big-move line sits on the same event:
 * a match-4 or a short cascade stays light under that line, and a match-5
 * or a long cascade is the medium cheer.
 *
 * A later wave can also name its own match-4 or match-5. Playback skips
 * that request when a cheer is already going, and a medium request replaces
 * a light one that has not started yet.
 *
 * Ordinary tricks are the self-buffs in QUIET_POWERUPS. Anything else that
 * lands is a strong trick. A blocked cast is quiet.
 */
export function applauseForMove(event: GameEvent, player: 0 | 1): ApplausePool | null {
  if (player !== 0) return null;
  if (event.type === 'match') {
    if (event.size === 4) return 'light';
    if (event.size >= 5) return 'medium';
    return null;
  }
  if (event.type === 'cascade') {
    if (event.index >= 3) return 'medium';
    if (event.index >= 1) return 'light';
    return null;
  }
  if (event.type === 'powerup') {
    if (event.blocked) return null;
    return QUIET_POWERUPS.has(event.id) ? 'light' : 'medium';
  }
  return null;
}

/** A uniform pick from the pool, skipping the clip that just played. */
export function pickApplause(pool: ApplausePool, last: string | null, random: () => number): string {
  const files = POOLS[pool];
  const available = files.filter((file) => file !== last);
  const choices = available.length > 0 ? available : files;
  const roll = random();
  const index = Math.min(choices.length - 1, Math.max(0, Math.floor(roll * choices.length)));
  return choices[index];
}
